import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useContactModal } from '../context/ContactModalContext';
import { t, translations } from '../data/translations';

// Animated search bar that cycles through job positions
const RotatingSearchBar = () => {
  const { language } = useLanguage();
  const { openContactModal } = useContactModal();
  const positions = translations.positions.list[language] || translations.positions.list.sk;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = positions[currentIndex];
    
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(displayText.slice(0, -1));
        } else {
          setIsDeleting(false);
          setCurrentIndex((prev) => (prev + 1) % positions.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentIndex, positions]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.7, delay: 0.3 }}
      className="relative w-full max-w-2xl mx-auto"
    >
      <div className="flex items-center bg-white rounded-full shadow-2xl overflow-hidden px-8 py-6 border-2 border-white/50">
        <Search className="w-7 h-7 text-[#131132]/40 mr-5 flex-shrink-0" />
        <div className="flex-1 text-left min-h-[32px] flex items-center">
          <span className="text-[#131132]/40 text-xl">{t('search.searchPlaceholder', language)} </span>
          <span className="text-[#131132] font-semibold text-xl ml-2">{displayText}</span>
          <span className="inline-block w-0.5 h-7 bg-[#E8A238] ml-1 animate-pulse" />
        </div>
        <motion.button
          onClick={openContactModal}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="w-14 h-14 bg-[#131132] rounded-full flex items-center justify-center ml-4 hover:bg-[#E8A238] transition-colors"
          data-testid="search-contact-button"
        >
          <ArrowRight className="w-6 h-6 text-white" />
        </motion.button>
      </div>
    </motion.div>
  );
};

const AIRealitySection = () => {
  const { language } = useLanguage();

  const stats = [
    { value: t('search.stat1Value', language), label: t('search.stat1Label', language) },
    { value: t('search.stat2Value', language), label: t('search.stat2Label', language) },
    { value: t('search.stat3Value', language), label: t('search.stat3Label', language) },
    { value: t('search.stat4Value', language), label: t('search.stat4Label', language) },
  ];

  return (
    <>
      {/* Big Statement + Search */}
      <section className="bg-[#F5EFE6] py-24 relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-[#131132] leading-[0.95] mb-4"
          >
            {t('search.headline1', language)}
          </motion.h2>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] mb-12"
          >
            <span 
              style={{
                background: 'linear-gradient(135deg, #E8A238 0%, #D97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {t('search.headline2', language)}
            </span>
          </motion.h2>
          
          <RotatingSearchBar />
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#131132] py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#E8A238] mb-2">
                  {stat.value}
                </div>
                <div className="text-white/60 text-lg">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default AIRealitySection;
